import type { ClientCardView } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useCallback, useMemo, useState } from "react"

import { useCabinetAccess, useHasPermission } from "../../../access/use-access"
import type { ClientVisitRow } from "../../../api/clients.api"
import { fetchClientCard, fetchClientVisits, revealClientPhone } from "../../../api/clients.api"
import { unmergeClient } from "../../../api/duplicates.api"
import { isApiProblem } from "../../../api/problem"
import { formatDateLong, formatTimeOfDay, todayInZone } from "../../../lib/format"
import { EmptyState } from "../../../ui/empty-state"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { initialsOf, masterAvatarStyle } from "../../../theme/master-color"
import { cx } from "../../../lib/cx"
import { visitsGenitive } from "../../../lib/plural"
import { Badge, Button, Card, Skeleton } from "../../../ui"
import styles from "../directories.module.css"
import { formatMoney } from "../format-money"
import { useResource } from "../use-resource"
import { lineOf } from "./consents-model"
import { ConsentsTab } from "./consents-tab"

/**
 * Карточка клиента (3-А2), три состояния: обычная, новый клиент, ограничение записи.
 *
 * ЧТО ЗДЕСЬ ГЛАВНОЕ.
 *
 * 1. СЧЁТЧИКИ НЕ ПРАВЯТСЯ РУКАМИ. Визиты, неявки, отмены и суммы считаются из истории;
 *    ошибочная неявка исправляется в самом визите, а не в карточке. Поле ввода рядом
 *    со счётчиком неявок было бы приглашением «подчистить» клиента перед разговором.
 *
 * 2. ОГРАНИЧЕНИЕ ОНЛАЙН-ЗАПИСИ — НЕ ЧЁРНЫЙ СПИСОК. Записать такого клиента по телефону
 *    по-прежнему можно, и это сказано прямо в плашке: решение принимает салон, а не
 *    программа.
 *
 * 3. ТЕЛЕФОН ОТКРЫВАЕТСЯ ДЕЙСТВИЕМ. Маска + «Показать» вместо номера — это не украшение:
 *    каждый показ пишется в аудит (POST clients/reveal-phone). Поэтому здесь не поле,
 *    а кнопка, и нажимает её человек осознанно.
 */

export function ClientCardScreen(props: {
  readonly clientId: string
  readonly onBack: () => void
  readonly onOpenClient: (clientId: string) => void
  readonly onOpenDuplicates: () => void
}): ReactNode {
  const { context } = useCabinetAccess()
  const canSeePhone = useHasPermission("clients.phone.view")
  const canBook = useHasPermission("appointments.create")
  const canMerge = useHasPermission("clients.merge")
  // Согласия видит тот, кто ими ведает (3-А5: «мастер этот экран не видит»). Сервер без
  // права их не отдаёт, и пустой блок читался бы как «согласий не давали» — неправда.
  const canSeeConsents = useHasPermission("clients.consents.manage")

  const branch = resolveBranch(context, null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone

  const card = useResource(
    (signal) => fetchClientCard(props.clientId, signal),
    `card:${props.clientId}`,
  )
  const visits = useResource(
    (signal) => fetchClientVisits(props.clientId, 50, signal),
    `visits:${props.clientId}`,
  )

  const [tab, setTab] = useState<CardTab>("visits")
  const [revealed, setRevealed] = useState<Readonly<Record<string, string>>>({})
  const [revealing, setRevealing] = useState<string | null>(null)

  const reveal = useCallback(
    async (phoneId: string): Promise<void> => {
      setRevealing(phoneId)
      try {
        const result = await revealClientPhone({ clientId: props.clientId })
        setRevealed((previous) => ({ ...previous, [phoneId]: result.phone }))
      } finally {
        setRevealing(null)
      }
    },
    [props.clientId],
  )

  /*
   * КАРТОЧКИ НЕТ — ЭТО НОРМАЛЬНЫЙ ЭКРАН, А НЕ СБОЙ (1-Э1.14). Ссылку на клиента
   * присылают в переписке, а карточку могли объединить с дублем или удалить по просьбе
   * человека (152-ФЗ). «Ошибка 404» здесь говорит про неисправность, которой нет,
   * и отправляет звонить в поддержку вместо того, чтобы вернуться к работе.
   */
  if (card.problem !== null && card.data === null && card.problem.status === 404) {
    return (
      <div className={styles.screen}>
        <EmptyState
          title="Такой карточки клиента больше нет"
          text="Её могли объединить с дублем или удалить по просьбе клиента. История визитов при объединении сохраняется в оставшейся карточке."
          action={{ label: "К списку клиентов", onClick: props.onBack }}
        />
      </div>
    )
  }

  if (card.problem !== null && card.data === null) {
    return <ProblemScreen problem={card.problem} onRetry={card.reload} />
  }

  const client = card.data

  return (
    <div className={styles.screen}>
      <div className={styles.crumbs}>
        <button type="button" className={styles.crumbLink} onClick={props.onBack}>
          Клиенты
        </button>
        <span aria-hidden="true">›</span>
        <span className={styles.crumbCurrent}>{client?.displayName ?? "Загружаем…"}</span>
        <div className={styles.headerActions}>
          <Button
            size="sm"
            deniedReason={canMerge ? undefined : "Объединять карточки может администратор или управляющий"}
            onClick={props.onOpenDuplicates}
          >
            Объединить дубли
          </Button>
          <Button
            size="sm"
            variant="primary"
            deniedReason={canBook ? undefined : "Записывать клиентов может администратор"}
          >
            Записать
          </Button>
        </div>
      </div>

      {client === null ? (
        <div className={styles.cardGrid}>
          <div className={styles.cardColumn}>
            <Card>
              <Skeleton width={220} height={19} radius={4} />
              <Skeleton width={320} height={11} radius={3} />
            </Card>
          </div>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          <div className={styles.cardColumn}>
            <MergeLinks
              client={client}
              timeZone={timeZone}
              canMerge={canMerge}
              onOpenClient={props.onOpenClient}
              onChanged={card.reload}
            />
            <ClientHead client={client} timeZone={timeZone} />
            <Card padded={false}>
              <CardTabs
                tab={tab}
                onTab={setTab}
                visitsCount={visits.loading ? null : (visits.data ?? []).length}
                notesCount={client.notes.length}
                canSeeConsents={canSeeConsents}
              />
              {tab === "visits" ? (
                <VisitsList
                  visits={visits.data}
                  loading={visits.loading}
                  timeZone={timeZone}
                  canBook={canBook}
                />
              ) : tab === "notes" ? (
                <NotesList client={client} timeZone={timeZone} />
              ) : tab === "consents" && canSeeConsents ? (
                <ConsentsTab
                  clientId={client.id}
                  clientName={client.displayName}
                  consents={client.consents}
                  hasTelegram={client.channels.some((channel) => channel.channel === "telegram")}
                  timeZone={timeZone}
                  onChanged={card.reload}
                />
              ) : (
                <LimitsPanel client={client} timeZone={timeZone} />
              )}
            </Card>
          </div>

          <div className={styles.cardColumn}>
            <Card>
              <h3 className={styles.sectionLabel}>Телефоны</h3>
              {client.phones.map((phone) => {
                const shown = revealed[phone.id] ?? phone.phone
                return (
                  <div key={phone.id} className={styles.sideRow}>
                    <div>
                      <div className={styles.sideValue}>{shown ?? phone.phoneMasked}</div>
                      <div className={styles.rowSub}>
                        {phone.isPrimary ? "основной" : "дополнительный"} ·{" "}
                        {phone.verifiedAt === null
                          ? "со слов, не подтверждён"
                          : `подтверждён кодом ${formatDateLong(phone.verifiedAt, timeZone)}`}
                      </div>
                    </div>
                    {shown === null || shown === undefined ? (
                      <Button
                        size="sm"
                        loading={revealing === phone.id}
                        loadingLabel="Открываем…"
                        deniedReason={
                          canSeePhone
                            ? undefined
                            : "Полный номер открывает администратор — каждый показ виден в журнале действий"
                        }
                        onClick={() => {
                          void reveal(phone.id)
                        }}
                      >
                        Показать
                      </Button>
                    ) : (
                      <Button size="sm">Копировать</Button>
                    )}
                  </div>
                )
              })}
              <span className={styles.rowSub}>
                Открытие номера останется в журнале действий салона.
              </span>
              <div className={styles.divider} />

              <h3 className={styles.sectionLabel}>Каналы связи</h3>
              {client.channels.length === 0 ? (
                <span className={styles.rowSub}>Каналов пока нет — напоминания уйдут по SMS.</span>
              ) : (
                client.channels.map((channel) => (
                  <div key={channel.id} className={styles.sideRow}>
                    <span>{CHANNEL_NAMES[channel.channel] ?? channel.channel}</span>
                    <Badge tone={channel.isVerified ? "ok" : "gray"}>
                      {channel.isVerified
                        ? "подтверждён"
                        : channel.subscribed
                          ? "не подтверждён"
                          : "отписан"}
                    </Badge>
                  </div>
                ))
              )}
            </Card>

            <UsualCard visits={visits.data} />

            {canSeeConsents && (
              <Card>
                <h3 className={styles.sectionLabel}>Согласия</h3>
                <ConsentRow
                  title="Обработка данных"
                  consent={lineOf(client.consents, "pd").current}
                  timeZone={timeZone}
                />
                <ConsentRow
                  title="Рекламные рассылки"
                  consent={lineOf(client.consents, "ads").current}
                  timeZone={timeZone}
                />
                <button type="button" className={styles.crumbLink} onClick={() => setTab("consents")}>
                  Подробно: редакции, история, отметить или отозвать
                </button>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const CHANNEL_NAMES: Readonly<Record<string, string>> = {
  telegram: "Telegram-бот",
  max: "MAX",
  mobile: "Приложение",
  email: "Почта",
  sms: "SMS",
}

function ConsentRow(props: {
  readonly title: string
  readonly consent: ClientCardView["consents"][number] | null
  readonly timeZone: string
}): ReactNode {
  const { consent } = props
  return (
    <div className={styles.sideRow}>
      <span>{props.title}</span>
      {consent === null ? (
        <span className={styles.sideMuted}>не запрашивалось</span>
      ) : consent.revokedAt !== null ? (
        <span className={styles.sideMuted}>отозвано {formatDateLong(consent.revokedAt, props.timeZone)}</span>
      ) : (
        <span className={styles.sideOk}>
          дано {formatDateLong(consent.grantedAt, props.timeZone)} · {consent.version}
        </span>
      )}
    </div>
  )
}

/**
 * Связи слияния (3-А7): слитая карточка говорит, куда ушла её история, выжившая — кого
 * в неё слили. Отменяют слияние отсюда: ошибку замечают в выжившей («откуда чужие визиты?»).
 */
function MergeLinks(props: {
  readonly client: ClientCardView
  readonly timeZone: string
  readonly canMerge: boolean
  readonly onOpenClient: (clientId: string) => void
  readonly onChanged: () => void
}): ReactNode {
  const [busy, setBusy] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const { client } = props
  if (client.mergedIntoId === null && client.mergedFrom.length === 0) return null

  const undo = (mergedId: string): void => {
    setBusy(mergedId)
    setProblem(null)
    unmergeClient(mergedId)
      .then(props.onChanged)
      .catch((error: unknown) => {
        if (!isApiProblem(error)) throw error
        setProblem(error.detail ?? error.title)
      })
      .finally(() => setBusy(null))
  }
  const denied = props.canMerge ? undefined : "Отменять слияние может администратор или управляющий"

  return (
    <Card>
      {client.mergedIntoId === null ? null : (
        <div className={styles.sideRow}>
          <span>
            Эта карточка объединена с «{client.mergedIntoName ?? "другой карточкой"}»: её визиты и телефоны — там.
          </span>
          <Button
            size="sm"
            onClick={() => {
              if (client.mergedIntoId !== null) props.onOpenClient(client.mergedIntoId)
            }}
          >
            Открыть
          </Button>
          <Button size="sm" deniedReason={denied} loading={busy === client.id} onClick={() => undo(client.id)}>
            Отменить слияние
          </Button>
        </div>
      )}
      {client.mergedFrom.map((item) => (
        <div key={item.id} className={styles.sideRow}>
          <span>
            В эту карточку объединена «{item.displayName}» — {formatDateLong(item.mergedAt, props.timeZone)}.
          </span>
          <Button size="sm" deniedReason={denied} loading={busy === item.id} onClick={() => undo(item.id)}>
            Отменить слияние
          </Button>
        </div>
      ))}
      {problem === null ? null : <div className={styles.dangerBox}>{problem}</div>}
    </Card>
  )
}

function ClientHead(props: { readonly client: ClientCardView; readonly timeZone: string }): ReactNode {
  const { client } = props
  const pinned = client.notes.filter((note) => note.isPinned)
  const blacklist = client.blacklist

  const stats: readonly { readonly key: string; readonly value: string; readonly alert?: boolean }[] = [
    { key: "Визиты", value: String(client.stats.visitsCount) },
    { key: "Неявки", value: String(client.stats.noShowCount), alert: client.stats.noShowCount > 0 },
    { key: "Отмены клиентом", value: String(client.stats.cancelledCount) },
    // Деньги гасит сервер по праву finance.view: null — это «не показываем», а не ноль.
    { key: "Оставлено", value: formatMoney(client.stats.ltvMinor) ?? "—" },
    { key: "Средний чек", value: formatMoney(client.stats.avgCheckMinor) ?? "—" },
  ]

  const isNew = client.stats.visitsCount === 0

  return (
    <Card>
      <div className={styles.clientHead}>
        <div className={styles.avatar} style={masterAvatarStyle(null)}>
          {initialsOf(client.displayName)}
        </div>
        <div className={styles.rowGrow}>
          <h2 className={styles.clientName}>{client.displayName}</h2>
          <div className={styles.clientSub}>
            {[
              client.birthDate === null ? null : `${ageOf(client.birthDate)}`,
              `в базе с ${formatDateLong(client.createdAt, props.timeZone)}`,
              client.source === null ? null : `источник: ${client.source}`,
            ]
              .filter((part): part is string => part !== null)
              .join(" · ")}
          </div>
          <div className={cx(styles.tagRow, styles.tagRowTop)}>
            {client.tags.map((tag) => (
              <Badge key={tag.id} tone="gray">
                {tag.name}
              </Badge>
            ))}
            <Badge tone="outline">+ метка</Badge>
          </div>
        </div>
      </div>

      {pinned.map((note) => (
        <div key={note.id} className={styles.pinnedNote}>
          <b aria-hidden="true">!</b>
          <span>
            {note.body} — закреплённая заметка
            {note.authorName === null ? "" : ` · ${note.authorName}`}
          </span>
        </div>
      ))}

      {blacklist === null ? null : (
        <div className={styles.dangerBox}>
          <b>Онлайн-запись ограничена</b> с {formatDateLong(blacklist.createdAt, props.timeZone)},{" "}
          {blacklist.expiresAt === null
            ? "бессрочно"
            : `до ${formatDateLong(blacklist.expiresAt, props.timeZone)}`}{" "}
          · причина: {blacklist.reason}
          {blacklist.createdByName === null ? "" : ` · ${blacklist.createdByName}`}. По телефону
          записать по-прежнему можно — решение за вами.
        </div>
      )}

      <div className={styles.stats}>
        {stats.map((stat) => (
          <div key={stat.key} className={styles.stat}>
            <div className={styles.statKey}>{stat.key}</div>
            <div
              className={cx(
                styles.statValue,
                isNew && styles.statValueEmpty,
                !isNew && stat.alert === true && styles.statValueAlert,
              )}
            >
              {isNew ? "—" : stat.value}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.rowSub}>
        Счётчики считаются из истории визитов и руками не правятся. Неверная неявка
        исправляется в самом визите — счётчик пересчитается.
      </div>
    </Card>
  )
}

function ageOf(birthDate: string): string {
  const born = new Date(`${birthDate}T12:00:00Z`)
  const years = Math.floor((Date.now() - born.getTime()) / (365.2425 * 24 * 3600 * 1000))
  const mod100 = years % 100
  const mod10 = years % 10
  const word =
    mod100 >= 11 && mod100 <= 14 ? "лет" : mod10 === 1 ? "год" : mod10 >= 2 && mod10 <= 4 ? "года" : "лет"
  return `${years} ${word}`
}

const VISIT_STATUS: Readonly<Record<string, { readonly label: string; readonly tone: "ok" | "gray" | "error" | "warn" }>> = {
  pending: { label: "Ждёт подтверждения", tone: "warn" },
  confirmed: { label: "Подтверждена", tone: "ok" },
  in_progress: { label: "В работе", tone: "ok" },
  completed: { label: "Завершён", tone: "gray" },
  cancelled: { label: "Отменён", tone: "gray" },
  no_show: { label: "Не пришёл", tone: "error" },
}

type CardTab = "visits" | "notes" | "consents" | "limits"

/**
 * Вкладки карточки (3-А2): «Визиты», «Заметки и метки», «Согласия», «Ограничения».
 * «Согласия» — только тому, кто ими ведает: мастер этот экран не видит (3-А5), и пустая
 * вкладка читалась бы как «согласий не давали».
 */
function CardTabs(props: {
  readonly tab: CardTab
  readonly onTab: (tab: CardTab) => void
  readonly visitsCount: number | null
  readonly notesCount: number
  readonly canSeeConsents: boolean
}): ReactNode {
  const tabs: readonly { readonly id: CardTab; readonly label: string; readonly count?: string }[] = [
    { id: "visits", label: "Визиты", count: props.visitsCount === null ? "…" : String(props.visitsCount) },
    { id: "notes", label: "Заметки и метки", count: String(props.notesCount) },
    ...(props.canSeeConsents ? [{ id: "consents" as const, label: "Согласия" }] : []),
    { id: "limits", label: "Ограничения" },
  ]
  return (
    <div className={styles.tabs} role="tablist">
      {tabs.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={props.tab === item.id}
          className={cx(styles.tab, props.tab === item.id && styles.tabActive)}
          onClick={() => props.onTab(item.id)}
        >
          {item.label}
          {item.count === undefined ? null : <span className={styles.tabCount}>{item.count}</span>}
        </button>
      ))}
    </div>
  )
}

function VisitsList(props: {
  readonly visits: readonly ClientVisitRow[] | null
  readonly loading: boolean
  readonly timeZone: string
  readonly canBook: boolean
}): ReactNode {
  const visits = props.visits ?? []
  const today = todayInZone(props.timeZone)

  if (props.loading) {
    return (
      <div className={styles.blockPad}>
        <Skeleton width="60%" height={11} radius={3} />
      </div>
    )
  }
  if (visits.length === 0) {
    return (
      <div className={styles.emptyTab}>
        <div className={styles.rowTitle}>Визитов ещё не было</div>
        <div className={styles.rowSub}>
          Согласий не зафиксировано — при следующей записи администратор отметит согласие
          на обработку данных.
        </div>
        <Button
          size="sm"
          variant="primary"
          deniedReason={props.canBook ? undefined : "Записывать клиентов может администратор"}
        >
          Записать
        </Button>
      </div>
    )
  }
  return (
    <>
      {visits.map((visit) => {
        const status = VISIT_STATUS[visit.status] ?? { label: visit.status, tone: "gray" as const }
        const date = todayInZone(props.timeZone, new Date(visit.startAt))
        return (
          <div key={visit.id} className={styles.visitRow}>
            <div className={styles.visitWhen}>
              <div className={styles.rowTitle}>
                {date === today ? "Сегодня" : formatDateLong(visit.startAt, props.timeZone)}
              </div>
              <div className={styles.rowSub}>
                {formatTimeOfDay(visit.startAt, props.timeZone)}–
                {formatTimeOfDay(visit.endAt, props.timeZone)}
              </div>
            </div>
            <div className={styles.rowGrow}>
              <div className={styles.rowTitle}>{visit.serviceNames.join(", ")}</div>
              <div className={styles.rowSub}>{visit.employeeName ?? "Без мастера"}</div>
            </div>
            <Badge tone={status.tone}>{status.label}</Badge>
            <div className={styles.rowPrice}>{formatMoney(visit.priceMinor) ?? "—"}</div>
            <Button size="sm">Ещё раз</Button>
          </div>
        )
      })}
    </>
  )
}

/**
 * Заметки и метки (3-А4) — пока чтение: закреплённые сверху, приватные помечены. Правка
 * заметок и меток — отдельный экран раздела, вкладка не обещает того, чего не делает.
 */
function NotesList(props: { readonly client: ClientCardView; readonly timeZone: string }): ReactNode {
  const notes = props.client.notes.toSorted((a, b) => Number(b.isPinned) - Number(a.isPinned))
  return (
    <div className={styles.blockPad}>
      <div className={styles.tagRow}>
        {props.client.tags.length === 0 ? (
          <span className={styles.rowSub}>Меток нет</span>
        ) : (
          props.client.tags.map((tag) => (
            <Badge key={tag.id} tone="gray">
              {tag.name}
            </Badge>
          ))
        )}
      </div>
      {notes.length === 0 ? (
        <div className={styles.rowSub}>Заметок нет. Аллергию и противопоказания пишут сюда и закрепляют.</div>
      ) : (
        notes.map((note) => (
          <div key={note.id} className={styles.visitRow}>
            <div className={styles.rowGrow}>
              <div className={styles.rowTitle}>{note.body}</div>
              <div className={styles.rowSub}>
                {[
                  note.authorName,
                  formatDateLong(note.createdAt, props.timeZone),
                  note.isPinned ? "закреплена" : null,
                  note.isPrivate ? "видна только автору" : null,
                ]
                  .filter((part): part is string => part !== null)
                  .join(" · ")}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  )
}

/** Ограничения (3-А6): действующее ограничение онлайн-записи или его отсутствие словами. */
function LimitsPanel(props: { readonly client: ClientCardView; readonly timeZone: string }): ReactNode {
  const blacklist = props.client.blacklist
  if (blacklist === null) {
    return (
      <div className={styles.emptyTab}>
        <div className={styles.rowTitle}>Ограничений нет</div>
        <div className={styles.rowSub}>Клиент записывается онлайн как обычно.</div>
      </div>
    )
  }
  return (
    <div className={styles.blockPad}>
      <div className={styles.dangerBox}>
        <b>Онлайн-запись ограничена</b> с {formatDateLong(blacklist.createdAt, props.timeZone)},{" "}
        {blacklist.expiresAt === null ? "бессрочно" : `до ${formatDateLong(blacklist.expiresAt, props.timeZone)}`}{" "}
        · причина: {blacklist.reason}
        {blacklist.createdByName === null ? "" : ` · ${blacklist.createdByName}`}. По телефону записать
        по-прежнему можно — решение за вами.
      </div>
    </div>
  )
}

/**
 * «Обычно» — подсказка администратору в разговоре: «вы обычно у Анны, записываем к ней?».
 * Считается из истории визитов, потому что отдельного поля «любимый мастер» нет
 * и быть не должно: оно разошлось бы с историей на второй же месяц.
 */
function UsualCard(props: { readonly visits: readonly ClientVisitRow[] | null }): ReactNode {
  const stats = useMemo(() => {
    const visits = props.visits ?? []
    if (visits.length === 0) return null

    const byMaster = new Map<string, number>()
    const byService = new Map<string, number>()
    for (const visit of visits) {
      if (visit.employeeName !== null) {
        byMaster.set(visit.employeeName, (byMaster.get(visit.employeeName) ?? 0) + 1)
      }
      for (const name of visit.serviceNames) {
        byService.set(name, (byService.get(name) ?? 0) + 1)
      }
    }

    const top = [...byMaster.entries()].toSorted((a, b) => b[1] - a[1])[0] ?? null
    const services = [...byService.entries()].toSorted((a, b) => b[1] - a[1]).slice(0, 3)
    return { top, services, total: visits.length }
  }, [props.visits])

  if (stats === null || stats.top === null) return null

  return (
    <Card>
      <h3 className={styles.sectionLabel}>Обычно</h3>
      <div>
        <b>{stats.top[0]}</b> — {stats.top[1]} из {visitsGenitive(stats.total)}
        <div className={styles.rowSub}>
          {stats.services.map(([name, count]) => `${name} · ${count}`).join(", ")}
        </div>
      </div>
    </Card>
  )
}
